import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { Copy, Mail } from "lucide-react";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import { Suspense, useState } from "react";
import { toast } from "sonner";
import curvedDashedStroke from "@/assets/etc/curved-dashed-stroke.svg";
import referralGraphic from "@/assets/graphics/referral.png";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { env } from "../../../../env";

export const Route = createFileRoute("/_authed/_org/referrals")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const location = useLocation();
  const { trpc } = Route.useRouteContext();
  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const { data: referrals, isLoading } = useQuery(
    trpc.referral.list.queryOptions(),
  );
  const [copied, setCopied] = useState(false);

  const referralLink = `${env.VITE_CLIENT_BASE_URL}/sign-up?referral=${user?.referralCode}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);

      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {}
  };

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/referrals",
          });
        }
      }}
      open={
        location.pathname !== "/referrals" &&
        location.pathname.includes("/referrals")
      }
    >
      <Page heading="Referrals">
        <div className="flex max-w-5xl flex-col items-center self-center @5xl/page:px-0 px-8 pb-24">
          <div className="mt-12 flex @2xl/page:h-48 @2xl/page:flex-row flex-col-reverse @2xl/page:gap-8 overflow-hidden rounded-lg border border-border @2xl/page:pl-8">
            <div className="flex flex-col @2xl/page:px-0 px-4 py-6">
              <h2 className="text-nowrap font-semibold @md/page:text-xl text-neutral-900">
                Give a Nonprofit $150 and Get $150
              </h2>
              <p className="mt-2 @2xl/page:w-[45ch] text-muted-foreground text-sm">
                Refer a nonprofit leader to Givefront. When they sign up and
                spend $1,000 with Givefront, they'll receive a $150 gift card,
                and you'll get one too.{" "}
                <a
                  href="https://www.givefront.com/legal/referrals"
                  target="_blank"
                  rel="noopener"
                  className="underline"
                >
                  Terms
                </a>
              </p>

              <div className="mt-4 flex items-center gap-4">
                <Link to="/referrals/send">
                  <Button size="sm">
                    <Mail className="h-4 w-4" />
                    Invite via Email
                  </Button>
                </Link>

                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4" />
                  {copied ? "Copied!" : "Copy Referral Link"}
                </Button>
              </div>
            </div>

            <img
              src={referralGraphic}
              alt="Referral Program Graphic"
              className="object-cover object-left"
            />
          </div>

          {!isLoading && Boolean(referrals?.length) && (
            <>
              <SectionHeader icon="users-round" title="My Referrals" />

              {referrals?.map((referral) => (
                <div
                  key={referral.id}
                  className="mb-4 flex w-full items-center justify-between rounded border border-border p-4"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-neutral-900">
                      {referral.email}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      Invited on{" "}
                      {referral.createdAt.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>

                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-medium text-blue-800 text-xs capitalize">
                    {referral.status}
                  </span>
                </div>
              ))}
            </>
          )}

          <SectionHeader title="How It Works" icon="info" />

          <div className="flex @2xl/page:flex-row flex-col items-center justify-center @2xl/page:gap-0 gap-8">
            <ReferralStep
              icon="mail"
              title="Invite a Nonprofit"
              description="Send a referral invitation below to a nonprofit leader who is not yet using Givefront."
            />

            <img
              src={curvedDashedStroke}
              alt=""
              className="-mx-12 @2xl/page:block hidden @4xl/page:w-64 w-32 select-none self-start"
            />

            <ReferralStep
              icon="user-plus"
              title="They Register"
              description="Once your referral joins and spends $1,000 with Givefront Cards, you'll be eligible for your reward."
            />

            <img
              src={curvedDashedStroke}
              alt=""
              className="-mx-12 -scale-100 mt-12 @2xl/page:block hidden @4xl/page:w-64 w-32 select-none self-start"
            />

            <ReferralStep
              icon="gift"
              title="Get Rewards"
              description="Check your inbox for an email with further instructions on claiming your reward!"
            />
          </div>
        </div>
      </Page>

      <SheetContent
        className="w-full"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Suspense fallback={<SheetSkeleton />}>
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}

type Props = {
  icon: IconName;
  title: string;
  description: string;
};

function ReferralStep({ icon, title, description }: Props) {
  return (
    <div className="flex @2xl/page:flex-col items-center @2xl/page:gap-0 gap-4 text-center">
      <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <DynamicIcon name={icon} className="text-primary" />
      </div>
      <div className="flex flex-col text-left @2xl/page:text-center">
        <h3 className="mt-2 font-semibold text-sm">{title}</h3>
        <p className="mt-1 @2xl/page:max-w-56 text-muted-foreground text-sm">
          {description}
        </p>
      </div>
    </div>
  );
}

type SectionHeaderProps = {
  icon: IconName;
  title: string;
};

function SectionHeader({ icon, title }: SectionHeaderProps) {
  return (
    <h1 className="mt-16 @2xl/page:mb-16 mb-8 flex w-full @md/page:flex-row flex-col items-center justify-center gap-4 font-semibold text-3xl text-neutral-800">
      <div className="flex items-center gap-4 text-nowrap">
        <DynamicIcon
          name={icon}
          className="size-7 shrink-0 text-neutral-600"
          strokeWidth={2.25}
        />

        {title}
      </div>
      <span className="ml-4 h-px @md/page:w-auto w-full @md/page:grow bg-border" />
    </h1>
  );
}
